import { isEmpty } from 'lodash';
import {
    BehaviorSubject,
    Observable,
    Observer,
    Subscribable,
    Unsubscribable,
    map,
    tap,
} from 'rxjs';

export abstract class State<T> implements Subscribable<T> {
    [key: string]: any;
    private obs$: any;

    constructor() {
        this.hydrate();
    }

    get isPersist() {
        return 'key' in this;
    }

    private hydrate() {
        if (this.isPersist) {
            try {
                const data = localStorage.getItem(this.key());
                const result: any = JSON.parse(data);
                for (const key in result) {
                    this[key] = result[key];
                }
            } catch (e) {
                localStorage.setItem(this.key(), this.toObject(true, {}));
            }
        }
        this.obs$ = new BehaviorSubject(this);
    }

    store(data: T): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const result: any = data;
                for (const key in result) {
                  this[key] = result[key];
                }
                if (this.isPersist) localStorage.setItem(this.key(), this.toJson(true));

                this.obs$?.next(this);
                resolve(this);
            } catch (e) {
                reject(e);
            }
        });
    }

    save(): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                if (this.isPersist)
                    localStorage.setItem(this.key(), this.toObject(true));
                this.obs$.next(this);
                resolve(this);
            } catch (e) {
                reject(e);
            }
        });
    }

    subscribe(
        observer: Partial<Observer<T>> | ((value: T) => void)
    ): Unsubscribable {
        try {
            return this.obs$.subscribe(observer);
        } catch (e) {
            throw e;
        }
    }

    clear(): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                for (const key in this) {
                    if (this.ignoreVar(key)) continue;
                    this[key] = null;
                }

                if (this.isPersist)
                    resolve(localStorage.removeItem(this.key()));
                this.obs$.next(this);
            } catch (e) {
                reject(e);
            }
        });
    }

    resolveStore<A>(resolver: (value: A) => T | null = null) {
        return (source: Observable<A>): Observable<A> => {
            return source.pipe(
                tap((value: A) => {
                    if(resolver) {
                        return this.store(resolver(value));
                    }
                    
                    if (!("storeResolver" in this)){
                        throw new Error(`Store Resolver not available in ${this.constructor.name}`);
                    };
                    try {
                        return this.store(this.storeResolver(value));
                    } catch (e) {}
                })
            );
        };
    }

    toObject(stringify = false, data = null) {
        const json: any = {};
        const source = !data ? this : data;
        for (const key in source) {
            if (this.ignoreVar(key)) continue;
            json[key] = source[key];
        }
        return stringify ? JSON.stringify(json) : json;
    }

    private ignoreVar(key: string) {
        return key.startsWith('_') || key.endsWith('$');
    }
}
